import { DeleteApi, GetApi, PostApi, PutApi } from './api';

/**
 * Create a new custom offering
 * @param {Object} payload - Custom offering data
 * @returns {Promise} Response data
 */
export const createCustomOffering = (payload, isHeader = true) => {
  return PostApi('admin/v1/offerings/custom-offerings', payload, isHeader).then((response) => {
    return response.data;
  });
};

/**
 * Update an existing custom offering
 * @param {string} id - Offering ID from query params
 * @param {Object} payload - Updated offering data
 * @returns {Promise} Response data
 */
export const updateCustomOffering = (id, payload, isHeader = true) => {
  return PutApi(`admin/v1/offerings/custom-offerings?id=${id}`, payload, isHeader).then(
    (response) => {
      return response.data;
    }
  );
};

/**
 * Get custom offerings with pagination and filters
 * @param {Object} params - Query parameters (page, limit, search, status, etc.)
 * @returns {Promise} Response data
 */
export const getCustomOfferings = (params, isHeader = true) => {
  let query = '';
  if (params) {
    const queryParams = new URLSearchParams(params).toString();
    query = `?${queryParams}`;
  }
  return GetApi(`admin/v1/offerings/custom-offerings${query}`, isHeader).then((response) => {
    return response.data;
  });
};
/**
 * Get custom offerings with pagination and filters
 * @param {Object} params - Query parameters (page, limit, search, status, etc.)
 * @returns {Promise} Response data
 */
export const getUsersCustomOfferings = (id, params, isHeader = true) => {
  let query = '';
  if (params) {
    const queryParams = new URLSearchParams(params).toString();
    query = `?${queryParams}`;
  }
  return GetApi(`admin/v1/offerings/custom-offerings/${id}/users${query}`, isHeader).then(
    (response) => {
      return response.data;
    }
  );
};

/**
 * Get a single custom offering by ID
 * @param {string} id - Offering ID
 * @returns {Promise} Response data
 */
export const getCustomOfferingById = (id, isHeader = true) => {
  return GetApi(`admin/v1/offerings/custom-offerings/${id}`, isHeader).then((response) => {
    return response.data;
  });
};

/**
 * Delete a custom offering
 * @param {string} id - Offering ID
 * @returns {Promise} Response data
 */
export const deleteCustomOffering = (id, isHeader = true) => {
  return DeleteApi(`admin/v1/offerings/custom-offerings/${id}`, isHeader).then((response) => {
    return response.data;
  });
};
/**
 * Create a new custom offering
 * @param {Object} payload - Custom offering data
 * @returns {Promise} Response data
 */
export const modifyUsersCustomOffering = (id, payload, isHeader = true) => {
  return PostApi(`admin/v1/offerings/custom-offerings/${id}/enroll`, payload, isHeader).then(
    (response) => {
      return response.data;
    }
  );
};

/**
 * COMPANY-SPECIFIC API FUNCTIONS
 * These functions are for company admins to manage their own custom offerings
 */

/**
 * Create a new custom offering (Company)
 * @param {Object} payload - Custom offering data
 * @returns {Promise} Response data
 */
export const createCompanyCustomOffering = (payload, isHeader = true) => {
  return PostApi('company/v1/offerings/custom-offerings', payload, isHeader).then((response) => {
    return response.data;
  });
};

/**
 * Update an existing custom offering (Company)
 * @param {string} id - Offering ID
 * @param {Object} payload - Updated offering data
 * @returns {Promise} Response data
 */
export const updateCompanyCustomOffering = (id, payload, isHeader = true) => {
  return PutApi(`company/v1/offerings/custom-offerings?id=${id}`, payload, isHeader).then(
    (response) => {
      return response.data;
    }
  );
};

/**
 * Get custom offerings with pagination and filters (Company)
 * @param {Object} params - Query parameters (page, limit, search, status, etc.)
 * @returns {Promise} Response data
 */
export const getCompanyCustomOfferings = (params, isHeader = true) => {
  let query = '';
  if (params) {
    const queryParams = new URLSearchParams(params).toString();
    query = `?${queryParams}`;
  }
  return GetApi(`company/v1/offerings/custom-offerings${query}`, isHeader).then((response) => {
    return response.data;
  });
};

/**
 * Get users for a custom offering (Company)
 * @param {string} id - Offering ID
 * @param {Object} params - Query parameters
 * @returns {Promise} Response data
 */
export const getCompanyUsersCustomOfferings = (id, params, isHeader = true) => {
  let query = '';
  if (params) {
    const queryParams = new URLSearchParams(params).toString();
    query = `?${queryParams}`;
  }
  return GetApi(`company/v1/offerings/custom-offerings/${id}/users${query}`, isHeader).then(
    (response) => {
      return response.data;
    }
  );
};

/**
 * Get a single custom offering by ID (Company)
 * @param {string} id - Offering ID
 * @returns {Promise} Response data
 */
export const getCompanyCustomOfferingById = (id, isHeader = true) => {
  return GetApi(`company/v1/offerings/custom-offerings/${id}`, isHeader).then((response) => {
    return response.data;
  });
};

/**
 * Delete a custom offering (Company)
 * @param {string} id - Offering ID
 * @returns {Promise} Response data
 */
export const deleteCompanyCustomOffering = (id, isHeader = true) => {
  return DeleteApi(`company/v1/offerings/custom-offerings/${id}`, isHeader).then((response) => {
    return response.data;
  });
};

/**
 * Modify users for a custom offering (Company)
 * @param {string} id - Offering ID
 * @param {Object} payload - User enrollment data
 * @returns {Promise} Response data
 */
export const modifyCompanyUsersCustomOffering = (id, payload, isHeader = true) => {
  return PostApi(`company/v1/offerings/custom-offerings/${id}/enroll`, payload, isHeader).then(
    (response) => {
      return response.data;
    }
  );
};

/**
 * Get enrolled users in a custom offering (Admin)
 * @param {string} id - Offering ID
 * @param {Object} params - Query parameters (page, limit, search, etc.)
 * @returns {Promise} Response data
 */
export const getAdminEnrolledUsersInOffering = (id, params, isHeader = true) => {
  let query = '';
  if (params) {
    const queryParams = new URLSearchParams(params).toString();
    query = `?${queryParams}`;
  }
  return GetApi(`admin/v1/offerings/custom-offerings/${id}/enrolled-users${query}`, isHeader).then(
    (response) => {
      return response.data;
    }
  );
};

/**
 * Get enrolled users in a custom offering (Company)
 * @param {string} id - Offering ID
 * @param {Object} params - Query parameters (page, limit, search, etc.)
 * @returns {Promise} Response data
 */
export const getCompanyEnrolledUsersInOffering = (id, params, isHeader = true) => {
  let query = '';
  if (params) {
    const queryParams = new URLSearchParams(params).toString();
    query = `?${queryParams}`;
  }
  return GetApi(
    `company/v1/offerings/custom-offerings/${id}/enrolled-users${query}`,
    isHeader
  ).then((response) => {
    return response.data;
  });
};

/**
 * Toggle promote to top for a custom offering (Company)
 * @param {string} id - Offering ID
 * @returns {Promise} Response data
 */
export const promoteCustomOfferingToTop = (id, isHeader = true) => {
  return PutApi(`company/v1/offerings/custom-offerings/${id}/promote`, {}, isHeader).then(
    (response) => {
      return response.data;
    }
  );
};

/**
 * Toggle promote to top for a custom offering (Admin)
 * @param {string} id - Offering ID
 * @returns {Promise} Response data
 */
export const promoteAdminCustomOfferingToTop = (id, isHeader = true) => {
  return PutApi(`admin/v1/offerings/custom-offerings/${id}/promote`, {}, isHeader).then(
    (response) => {
      return response.data;
    }
  );
};

/**
 * Get Gourmet Society offering status for a company
 * @param {string} companyId - Company ID
 * @returns {Promise} Response data
 */
export const getGourmetSocietyStatus = (companyId, isHeader = true) => {
  return GetApi(
    `admin/v1/ello/company-list?page=1&perPage=10&companyId=${companyId}`,
    isHeader
  ).then((response) => {
    return response.data;
  });
};

/**
 * Get users associated with Gourmet Society offer for a company
 * @param {string} companyId - Company ID
 * @param {Object} params - Query params ({ page, perPage, searchKey, isUsed })
 * @param {boolean} isHeader - Header flag
 * @returns {Promise} Response data
 */
export const getGourmetSocietyOfferUsers = (companyId, params = {}, isHeader = true) => {
  const queryParams = new URLSearchParams(
    Object.entries({ companyId, page: 1, perPage: 10, ...params }).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = String(value);
      }
      return acc;
    }, {})
  ).toString();

  return GetApi(`admin/v1/ello/offer-users?${queryParams}`, isHeader).then((response) => {
    return response.data;
  });
};

/**
 * Toggle one or more company features via the unified feature-toggle endpoint.
 * @param {Object} payload - { companyId: string, features: Array<{feature, enable, isAll?, count?}> }
 * @returns {Promise} Response data
 */
export const toggleCompanyFeature = (payload, isHeader = true) => {
  return PutApi('admin/v1/company/toggle-feature', payload, isHeader).then((response) => {
    return response.data;
  });
};

/**
 * Get all users of a company with their Gourmet Society (Ello) access status
 * @param {string} companyId - Company ID
 * @param {Object} params - Query params ({ page, perPage, search })
 * @returns {Promise} Response data
 */
export const getGourmetSocietyCompanyUsers = (companyId, params = {}, isHeader = true) => {
  const queryParams = new URLSearchParams(
    Object.entries({ companyId, page: 1, perPage: 10, ...params }).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = String(value);
      }
      return acc;
    }, {})
  ).toString();

  return GetApi(`admin/v1/ello/company-users?${queryParams}`, isHeader).then((response) => {
    return response.data;
  });
};

/**
 * Batch update Gourmet Society (Ello) access for specific users
 * @param {Object} payload - { companyId, addedUsers: string[], removedUsers: string[] }
 * @returns {Promise} Response data
 */
export const updateGourmetSocietyUserStatus = (payload, isHeader = true) => {
  return PostApi('admin/v1/ello/update-users', payload, isHeader).then((response) => {
    return response.data;
  });
};

/**
 * Get Ello / Gourmet Society dashboard stats
 * @returns {Promise} Response data
 */
export const getElloDashboard = (isHeader = true) => {
  return GetApi('admin/v1/ello/dashboard', isHeader).then((response) => {
    return response.data;
  });
};

export const getMarketplaceDashboard = (isHeader = true) => {
  return GetApi('admin/v1/marketplace/dashboard', isHeader).then((response) => {
    return response.data;
  });
};

export const getElloStats = (isHeader = true) => {
  return GetApi('admin/v1/ello/stats', isHeader).then((response) => {
    return response.data;
  });
};

export const getElloUserList = (params = {}, isHeader = true) => {
  const queryParams = new URLSearchParams(
    Object.entries(params).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = String(value);
      }
      return acc;
    }, {})
  ).toString();
  return GetApi(`admin/v1/ello/user-list?${queryParams}`, isHeader).then((response) => {
    return response.data;
  });
};

/**
 * Get Ello / Gourmet Society status for the logged-in company (B2B side)
 * @returns {Promise} Response data
 */
export const getElloStatus = (isHeader = true) => {
  return GetApi('company/v1/ello/status', isHeader).then((response) => {
    return response.data;
  });
};

/**
 * Get users who have Gourmet Society (Ello) access in the logged-in company (B2B side)
 * @param {Object} params - Query params ({ page, perPage, search })
 * @returns {Promise} Response data
 */
export const getElloEnabledUsers = (params = {}, isHeader = true) => {
  const queryParams = new URLSearchParams(
    Object.entries({ page: 1, perPage: 10, ...params }).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = String(value);
      }
      return acc;
    }, {})
  ).toString();

  return GetApi(`company/v1/ello/enabled-users?${queryParams}`, isHeader).then((response) => {
    return response.data;
  });
};

/**
 * Get all company users for Gourmet Society assignment (B2B side)
 * @param {Object} params - Query params ({ page, perPage, search })
 * @returns {Promise} Response data
 */
export const getElloUsersList = (params = {}, isHeader = true) => {
  const queryParams = new URLSearchParams(
    Object.entries({ page: 1, perPage: 10, ...params }).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = String(value);
      }
      return acc;
    }, {})
  ).toString();

  return GetApi(`company/v1/ello/users-list?${queryParams}`, isHeader).then((response) => {
    return response.data;
  });
};

/**
 * Assign Gourmet Society (Ello) access to selected users (B2B side)
 * @param {string[]} userIds - Array of user IDs to assign
 * @returns {Promise} Response data
 */
export const assignElloToUsers = (userIds, isHeader = true) => {
  return PostApi('company/v1/ello/assign-to-users', { userIds }, isHeader).then((response) => {
    return response.data;
  });
};

/**
 * Send a "Need Help" request for a specific user (B2B side)
 * @param {Object} payload - { userId, companyId, message }
 * @returns {Promise} Response data
 */
export const sendElloNeedHelp = (payload, isHeader = true) => {
  return PostApi('company/v1/ello/need-help', payload, isHeader).then((response) => {
    return response.data;
  });
};
